import { Component, Input } from "@angular/core";
import { FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoPipe, translate } from "@jsverse/transloco";
import { NzAvatarComponent } from "ng-zorro-antd/avatar";
import { NzButtonComponent } from "ng-zorro-antd/button";
import { NzCardComponent } from "ng-zorro-antd/card";
import {
	NzFormControlComponent,
	NzFormDirective,
	NzFormItemComponent,
	NzFormLabelComponent,
} from "ng-zorro-antd/form";
import { NzColDirective, NzRowDirective } from "ng-zorro-antd/grid";
import { NzInputDirective } from "ng-zorro-antd/input";
import { NzMessageService } from "ng-zorro-antd/message";
import { AuthApi } from "../../../../domain/api/auth.api";
import { CompanyApi } from "../../../../domain/api/company.api";
import { UserApi } from "../../../../domain/api/user.api";
import { UpdateCompanyDeliveryData } from "../../../../domain/command/company.command";
import { UpdateUser } from "../../../../domain/command/user.command";
import { sleepUpTo } from "../../../../domain/helper/sleep.helper";
import { uuid } from "../../../../domain/helper/uuid.helper";
import { Company } from "../../../../domain/model/company.model";
import { ROLE_FACTORY, User } from "../../../../domain/model/user.model";
import { FormBuilderService } from "../../../../domain/service/form/form.builder";
import { FormValidator } from "../../../../domain/service/form/form.validator";
import { ImageUploadProfileComponent } from "../../components/image-upload-profile.component";
import { CdnPipe } from "../../pipe/cdn.pipe";
import { FilePipe } from "../../pipe/file.pipe";
import { BasePageComponent } from "../base-page.component";

@Component({
	selector: "app-profile-form",
	imports: [
		NzCardComponent,
		ReactiveFormsModule,
		NzFormDirective,
		NzAvatarComponent,
		ImageUploadProfileComponent,
		CdnPipe,
		NzRowDirective,
		NzFormLabelComponent,
		NzFormControlComponent,
		NzInputDirective,
		NzButtonComponent,
		NzColDirective,
		NzFormItemComponent,
		FilePipe,
		TranslocoPipe,
	],
	template: `
    @if (form) {
      <nz-card [nzTitle]="'profile.form.title' | transloco" class="rounded w-full">
        <div class="w-full">
          <div class="flex gap-5">
            <form class="flex gap-5" nz-form [formGroup]="form" (keydown.enter)="$event.preventDefault()">
              <div class="flex flex-col items-center justify-center gap-2">
                <nz-avatar nzIcon="user" [nzSize]="64"
                           [nzSrc]="form.get('profileImageId')!.value | filePath | cdn"></nz-avatar>
                <app-image-upload-profile class="mt-6"
                                          (fileUploaded)="onImageUploaded($event)"/>
              </div>
              <div>
                <nz-form-item [nzGutter]="24">
                  <div class="w-1/2">
                    <nz-form-label nzRequired nzNoColon nzFor="name">{{ 'common.labels.name' | transloco }}</nz-form-label>
                    <nz-form-control>
                      <input nz-input formControlName="name" id="name"/>
                    </nz-form-control>
                  </div>
                  <div class="w-1/2">
                    <nz-form-label nzNoColon nzFor="lastName">{{ 'common.labels.lastName' | transloco }}</nz-form-label>
                    <nz-form-control>
                      <input nz-input formControlName="lastName" id="lastName"/>
                    </nz-form-control>
                  </div>
                  <div class="w-1/2">
                    <nz-form-label nzRequired nzNoColon nzFor="email">{{ 'common.labels.email' | transloco }}</nz-form-label>
                    <nz-form-control [nzErrorTip]="emailErrorTip">
                      <input nz-input type="email" formControlName="email" id="email"/>
                    </nz-form-control>
                    <ng-template #emailErrorTip>
                      @if (form.get("email")!.errors?.['emailExists']) {
                        <span>{{ 'profile.form.emailExists' | transloco }}</span>
                      }
                    </ng-template>
                  </div>
                </nz-form-item>
                <nz-form-item [nzGutter]="24">
                  @if (user?.role === ROLE_FACTORY && factoryForm) {
                    <form class="w-1/2" [formGroup]="factoryForm" (keydown.enter)="$event.preventDefault()">
                      <div class="w-full">
                        <nz-form-label nzNoColon>{{ 'profile.form.minimumDaysForDelivery' | transloco }}</nz-form-label>
                        <nz-form-control [nzErrorTip]="'profile.form.minimumDaysForDeliveryRequired' | transloco">
                          <input type="number" nz-input formControlName="minimumDaysForDelivery"/>
                        </nz-form-control>
                      </div>
                    </form>
                  }
                </nz-form-item>
              </div>
            </form>

          </div>
        </div>
        <div class="w-full flex justify-end mt-2">
          <button nz-button nzType="primary" class="h-full" (click)="submit()" [nzLoading]="submitLoading">
            {{ 'common.actions.save' | transloco }}
          </button>
        </div>
      </nz-card>
    }

  `,
	styles: [],
})
export class ProfileFormComponent extends BasePageComponent {
	@Input() user: User | undefined;
	@Input() company: Company | undefined;
	id = this.authApi.me()()!.id;
	submitLoading = false;
	form: FormGroup | undefined;
	factoryForm: FormGroup | undefined;

	protected readonly ROLE_FACTORY = ROLE_FACTORY;
	constructor(
		private readonly formBuilder: FormBuilderService,
		private readonly userApi: UserApi,
		private readonly message: NzMessageService,
		private readonly formValidator: FormValidator,
		private readonly authApi: AuthApi,
		private readonly companyApi: CompanyApi,
	) {
		super();
	}

	override onChanges() {
		super.onChanges();
		this.buildForm();
	}

	buildForm() {
		const newUserId = uuid();
		const validators = [
			{
				key: "email",
				validators: [Validators.email, Validators.required],
				asyncValidators: [
					this.formValidator.emailExistsValidator(this.user?.id),
				],
			},
			{
				key: "contactMail",
				validators: [Validators.email],
			},
			{ key: "name", validators: [Validators.required] },
		];
		this.form = this.formBuilder.build(
			{
				id: this.user?.id ?? newUserId,
				name: this.user?.name ?? "",
				lastName: this.user?.lastName ?? "",
				email: this.user?.email ?? "",
				profileImageId: this.user?.profileImageId,
			},
			validators,
		);
		if (this.user?.role === ROLE_FACTORY)
			this.factoryForm = this.formBuilder.build({
				minimumDaysForDelivery:
					this.company?.deliveryData.minimumDaysForDelivery,
			});
	}

	async submit() {
		if (!this.form) return;
		if (!this.form?.valid) {
			Object.values(this.form.controls).forEach((control) => {
				if (control.invalid) {
					control.markAsDirty();
					control.updateValueAndValidity({ onlySelf: true });
				}
			});
			this.message.error(translate("common.messages.missingFields"));
			return;
		}
		try {
			this.submitLoading = true;
			const command: UpdateUser = {
				id: this.form.value.id,
				email: this.form.value.email,
				lastName: this.form.value.lastName,
				name: this.form.value.name,
				password: "",
				profileImageId: this.form.value.profileImageId,
			};
			await sleepUpTo(300);
			await this.userApi.update(command);
			if (this.user?.role === ROLE_FACTORY) {
				const factoryCommand: UpdateCompanyDeliveryData = {
					minimumDaysForDelivery:
						this.factoryForm?.value.minimumDaysForDelivery,
					companyId: this.company?.id || "",
				};
				await this.companyApi.updateDeliveryData(factoryCommand);
			}
			this.message.success(translate("profile.form.saved"));
		} catch {
			this.message.error(translate("common.messages.genericError"));
		} finally {
			this.submitLoading = false;
		}
	}
	onImageUploaded(fileId: string) {
		if (!this.form) return;
		this.form.patchValue({ profileImageId: fileId });
	}
}
